import { adminApiAxios } from '@/api'
import type { DateTime } from 'luxon'
import { keyBy } from 'lodash'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import { fetchIfNeededWrapper, fetchWrapper } from '../../reducers/fetch'
import {
  FinancialAccountWithAdminInfo,
  receiveAdminDeleteUserFinancialAccount,
  receiveUserFinancialAccount,
  receiveUserFinancialAccounts,
} from '../../reducers/admin/financialAccountsReducer'
import {
  FinancialAccount,
  FinancialAccountPrimaryUse,
} from '../../reducers/finances/financialAccountsReducer'
import { Transaction } from '../../reducers/admin/allTransactions.slice'
import {
  receiveAdminDeleteUserPlaidItem,
  receiveUserPlaidItem,
  receiveUpdateUserPlaidItemFinancialAccount,
} from '../../reducers/admin/plaidItemReducer'
import { AdminPlaidItem } from '../../reducers/finances/plaidItemReducer'
import { fetchUserPlaidItems } from './adminPlaidItemActions'

export const REQUEST_USER_FINANCIAL_ACCOUNTS_KEY = (userId: number | string) =>
  `REQUEST_USER_FINANCIAL_ACCOUNTS_KEY_${userId}`

export const fetchUserFinancialAccounts = (
  userId: number | string,
  alwaysFetch = false
) =>
  fetchIfNeededWrapper({
    alwaysFetch,
    fetchKey: REQUEST_USER_FINANCIAL_ACCOUNTS_KEY(userId),
    defaultErrorMessage: 'There was an error fetching financial accounts',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<{
        accounts: FinancialAccountWithAdminInfo[]
        financialProfileId: number
      }>(`/finances/api/v1/admin/${userId}/accounts`)

      dispatch(
        receiveUserFinancialAccounts({
          accounts: keyBy(json.data.accounts, 'id'),
          userId: Number(userId),
        })
      )
      return true
    },
  })

export interface UpdateAdminUserFinancialAccount {
  excludeFromReports?: boolean
  inactive?: boolean
  bankAccessEnabledAt?: string | null
  /** Null hands the label back to the therapist's answer and sorting. */
  primaryUseEffective?: FinancialAccountPrimaryUse | null
}

export const updateAdminUserFinancialAccounts = (
  userId: number,
  accountId: number,
  data: UpdateAdminUserFinancialAccount
) =>
  fetchWrapper({
    fetchKey: REQUEST_USER_FINANCIAL_ACCOUNTS_KEY(userId),
    defaultErrorMessage: 'There was an error updating the financial account.',
    fetchFunction: async (dispatch) => {
      // Note that this does NOT come back with all admin financial account info
      const json = await adminApiAxios.put<FinancialAccount>(
        `/finances/api/v1/admin/accounts/${accountId}`,
        data
      )
      dispatch(
        receiveUserFinancialAccount({
          account: {
            ...json.data,
            reconciliations: [],
            accounts: null,
            manualAccount: null,
          },
          userId,
        })
      )
      dispatch(
        receiveUpdateUserPlaidItemFinancialAccount({
          userId,
          account: json.data,
        })
      )
      return true
    },
  })

export interface CreateUserManualFinancialAccount {
  financialProfileId: number
  plaidInstitutionName: string
  name: string
  mask: string
  type: string
  subtype: string
  plaidAccountId?: number
}
export const createUserManualFinancialAccount = (
  userId: number,
  data: CreateUserManualFinancialAccount
) =>
  fetchWrapper({
    fetchKey: REQUEST_USER_FINANCIAL_ACCOUNTS_KEY(userId),
    defaultErrorMessage: 'There was an error creating the financial account.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<{
          account: FinancialAccountWithAdminInfo
          financialProfileId: number
        }>(`/finances/api/v1/admin/${userId}/accounts`, {
          ...data,
          accountId: 'manual',
          accountType: 'manual',
        })
        .then((json) => {
          dispatch(
            receiveUserFinancialAccount({
              account: {
                ...json.data.account,
              },
              userId,
            })
          )
          return json.data
        }),
  })

export const adminDeleteFinancialAccount = (
  userId: number,
  financialAccountId: number
) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error removing the financial account.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .delete<{
          deletedId: number
          financialProfileId: number
        }>(`/finances/api/v1/admin/accounts/${financialAccountId}`)
        .then((json) => {
          dispatch(
            receiveAdminDeleteUserFinancialAccount({
              userId,
              deletedId: json.data.deletedId,
            })
          )
          return json.data
        }),
  })

interface TransactionCountParams {
  manual?: boolean
  endDate?: string
}

export const fetchTransactionCountsSinceLastReconciliation = (
  userId: number | string,
  params: TransactionCountParams = {}
) =>
  fetchWrapper({
    fetchFunction: () =>
      adminApiAxios
        .get<{
          [key: number]: number
        }>(`/finances/api/v1/admin/${userId}/accounts/new_transaction_counts`, {
          params,
        })
        .then((json) => json.data),
  })

/**
 * Per-account count of transactions that make the given report's reconciliation stale —
 * in-window changes since it was completed. Drives the monthly-books "needs updating" badge.
 */
export const fetchStaleTransactionCountsForReport = (
  userId: number | string,
  reportId: number
) =>
  fetchWrapper({
    fetchFunction: () =>
      adminApiAxios
        .get<{
          [key: number]: number
        }>(
          `/finances/api/v1/admin/${userId}/accounts/stale_transaction_counts`,
          {
            params: { reportId },
          }
        )
        .then((json) => json.data),
  })

/**
 * Mirrors the API's `ReconciliationUnavailableReason`. The panel renders the paired
 * message rather than mapping these to copy itself.
 */
export enum ReconciliationUnavailableReason {
  BooksComplete = 'books_complete',
  BlockedOnEarlierPeriod = 'blocked_on_earlier_period',
  LaterMonthsWaiting = 'later_months_waiting',
  PreviousManuallyReconciled = 'previous_manually_reconciled',
  InvalidPreviousReconciliation = 'invalid_previous_reconciliation',
  InvertedPeriod = 'inverted_period',
  AlreadySettled = 'already_settled',
  AccountTypeUnsupported = 'account_type_unsupported',
  NoQualifyingManualRecon = 'no_qualifying_manual_recon',
  NoAnchor = 'no_anchor',
  InvertedAnchor = 'inverted_anchor',
  AnchorGap = 'anchor_gap',
  ImportedTransactions = 'imported_transactions',
  ReportPeriodNotStarted = 'report_period_not_started',
  ReportHasNoMonth = 'report_has_no_month',
  NothingToRecover = 'nothing_to_recover',
  ReportPeriodNotEnded = 'report_period_not_ended',
  BeforeAutoReconLaunch = 'before_auto_recon_launch',
  AccountFeedDisconnected = 'account_feed_disconnected',
  AccountFeedStale = 'account_feed_stale',
  RetryInProgress = 'retry_in_progress',
  PeriodAlreadyReconciled = 'period_already_reconciled',
  AccountNotOpenInPeriod = 'account_not_open_in_period',
  AccountInactive = 'account_inactive',
}

export interface ReconciliationCapabilities {
  canAutoRecon: boolean
  canManual: boolean
  canRecover: boolean
  unavailableReason: {
    autoRecon?: ReconciliationUnavailableReason
    manual?: ReconciliationUnavailableReason
    recover?: ReconciliationUnavailableReason
  }
  unavailableMessage: {
    autoRecon?: string
    manual?: string
    recover?: string
  }
  accountFeed: {
    isDisconnected: boolean
    /** No transaction in over a week, whatever the institution. */
    isQuiet: boolean
    /** The narrower rule: the balance cannot be trusted, so auto-recon is withheld. */
    isUnreliable: boolean
    lastTransactionDate: string | null
  }
  period: PeriodState
}

/**
 * Mirrors the API's `PeriodState`: what state the period is in, as opposed to what may be
 * done with it.
 */
export interface PeriodState {
  reconciliationId: number | null
  status:
    | 'never_reconciled'
    | 'not_reconciled'
    | 'complete'
    | 'incomplete'
    | 'blocked'
    | 'retrying'
  /** `yyyy-MM` of the earliest month still holding a blocked period up. */
  blockedOnMonth: string | null
  retryWindowEndsAt: string | null
}

/**
 * Which reconciliation actions the server will accept per account in this report. An
 * account missing from the response has not been declared.
 */
export const fetchReconciliationCapabilities = (
  userId: number | string,
  reportId: number
) =>
  fetchWrapper({
    fetchFunction: () =>
      adminApiAxios
        .get<{
          [accountId: number]: ReconciliationCapabilities
        }>(
          `/finances/api/v1/admin/${userId}/accounts/reconciliation_capabilities`,
          {
            params: { reportId },
          }
        )
        .then((json) => json.data),
  })

export interface FinancialAccountSumResponse extends FinancialAccount {
  transactionsSumInCents: number
  transactionSumIds: number[]
}

export const FETCH_FINANCIAL_ACCOUNT_TRANSACTION_SUM_KEY =
  'FETCH_FINANCIAL_ACCOUNT_TRANSACTION_SUM_KEY'
export const fetchFinancialAccountTransactionSum = (
  financialAccountId: number,
  startDate: DateTime,
  endDate: DateTime
) =>
  fetchWrapper({
    fetchKey: FETCH_FINANCIAL_ACCOUNT_TRANSACTION_SUM_KEY,
    fetchFunction: () =>
      adminApiAxios
        .get<FinancialAccountSumResponse>(
          `/finances/api/v1/admin/accounts/account_sum/${financialAccountId}`,
          {
            params: {
              startDate: startDate.toFormat(DATE_FORMATS_LUXON.API_INPUT),
              endDate: endDate.toFormat(DATE_FORMATS_LUXON.API_INPUT),
              limit: 1000,
            },
          }
        )
        .then((json) => json.data),
  })

export const getDateOfLastTransaction = (financialAccountId: number) =>
  fetchWrapper({
    fetchFunction: () =>
      adminApiAxios
        .get<Transaction>(
          `/finances/api/v1/admin/accounts/${financialAccountId}/latest_transaction`
        )
        .then((json) => json.data),
  })

export const forcePullTransactions = (data: {
  plaidItemId?: number
  accountId?: number
}) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error updating the PlaidItem.',
    defaultValue: false,
    fetchFunction: () =>
      adminApiAxios
        .post<{
          transactionIds: number[]
        }>('/finances/api/v1/admin/plaid/force_pull_transactions', data)
        .then((json) => json.data),
  })

export interface ResetTransactionSyncCursorRequest {
  syncFromDate: string
  reason: string
}

export interface ResetTransactionSyncCursorResponse {
  auditLogId: number
  transactionIds: number[]
}

export const RESET_TRANSACTION_SYNC_CURSOR_KEY =
  'RESET_TRANSACTION_SYNC_CURSOR_KEY'
export const resetTransactionSyncCursor = (
  plaidItemId: number,
  userId: number,
  data: ResetTransactionSyncCursorRequest
) =>
  fetchWrapper({
    fetchKey: RESET_TRANSACTION_SYNC_CURSOR_KEY,
    defaultErrorMessage:
      'There was an error resetting the Plaid transaction sync.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<ResetTransactionSyncCursorResponse>(
          `/finances/api/v1/admin/plaid/items/${plaidItemId}/reset-cursor`,
          data
        )
        .then((json) => {
          dispatch(fetchUserPlaidItems(userId, true))
          return json.data
        }),
  })

export const forcePullStatements = (plaidItemId?: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error pulling new statements',
    defaultValue: false,
    fetchFunction: () =>
      adminApiAxios
        .post<{
          message: string
        }>('/finances/api/v1/admin/plaid/force_pull_statements', {
          plaidItemId,
        })
        .then((json) => json.data),
  })

export const forceNotifyUserReconnection = (data: {
  plaidItemId?: number
  accountId?: number
}) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error force notifying user.',
    defaultValue: false,
    fetchFunction: () =>
      adminApiAxios
        .post<{
          status: string
        }>('/finances/api/v1/admin/plaid/force_notify_reconnect', data)
        .then((json) => json.data),
  })

export const ADMIN_LINK_FINANCIAL_ACCOUNT_KEY =
  'ADMIN_LINK_FINANCIAL_ACCOUNT_KEY'

export const adminLinkFinancialAccount = (
  userId: number,
  accountId: number,
  data: {
    userId: number
    financialAccountId: number
    plaidAccountId: number
  }
) =>
  fetchWrapper({
    fetchKey: ADMIN_LINK_FINANCIAL_ACCOUNT_KEY,
    defaultErrorMessage: 'There was an error linking the manual account.',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.put<{
        manualAccount: FinancialAccount
        plaidAccount: FinancialAccountWithAdminInfo
      }>(`/finances/api/v1/admin/accounts/link/${accountId}`, data)
      dispatch(
        receiveAdminDeleteUserFinancialAccount({
          userId,
          deletedId: json.data.manualAccount.id,
        })
      )
      dispatch(
        receiveUserFinancialAccount({
          account: {
            ...json.data.plaidAccount,
          },
          userId,
        })
      )
      return true
    },
  })

export const markPlaidItemInactive = (plaidItemId: number, userId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error inactivating the Plaid Item.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<AdminPlaidItem>(
          `/finances/api/v1/admin/plaid/items/${plaidItemId}`
        )
        .then((json) => {
          dispatch(
            receiveUserPlaidItem({
              userId,
              plaidItem: json.data,
            })
          )
          dispatch(fetchUserFinancialAccounts(userId, true))
          return json.data
        }),
  })

export const deletePlaidItem = (plaidItemId: number, userId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error deleting the Plaid Item.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .delete<number>(`/finances/api/v1/admin/plaid/items/${plaidItemId}`)
        .then((json) => {
          dispatch(
            receiveAdminDeleteUserPlaidItem({
              userId,
              deletedId: plaidItemId,
            })
          )
          dispatch(fetchUserFinancialAccounts(userId, true))
          return json.data
        }),
  })

export const updatePlaidStatementsDisabled = (
  plaidItemId: number,
  userId: number,
  disable: boolean
) =>
  fetchWrapper({
    defaultErrorMessage:
      'There was an error updating Plaid Statements disable state.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<undefined>(
          `/finances/api/v1/admin/plaid/items/${plaidItemId}/plaid_statements`,
          { disable }
        )
        .then(() => {
          dispatch(fetchUserPlaidItems(userId, true))
          return true
        }),
  })
